import dayjs from "dayjs";
import {
  buildAddTenorCatalogParams,
  parseAddTenorCatalogId,
} from "constants/addTenorTable";
import {
  validateSublegMonthLabel,
  validateTenorLabelForPeriod,
} from "./addTenorTenorRules";
import {
  groupAddTenorRowsForSubmit,
  isAddTenorGroupedRow,
} from "./addTenorRowExpansion";

/**
 * @param {object} row
 * @returns {string[]} error messages
 */
export function validateAddTenorRow(row, shortcutLabel = "") {
  const errors = [];

  if (parseAddTenorCatalogId(row.product_id) == null) {
    errors.push("Product is required");
  }
  if (parseAddTenorCatalogId(row.contract_id) == null) {
    errors.push("Contract is required");
  }
  if (parseAddTenorCatalogId(row.style_id) == null) {
    errors.push("Style is required");
  }
  if (parseAddTenorCatalogId(row.period_type_id) == null) {
    errors.push("Period is required");
  }
  if (!String(row.tenor_label ?? "").trim()) {
    errors.push("Tenor is required");
  }

  const tenorLabel = row.tenor_label || "";
  const periodLabel = row.period_label || "";
  const formatError = validateTenorLabelForPeriod(tenorLabel, periodLabel);
  if (formatError) errors.push(formatError);

  const ulTenor = String(row.ul_tenor ?? "").trim();
  if (!ulTenor) {
    errors.push("UL Tenor is required");
  } else if (tenorLabel && ulTenor !== tenorLabel) {
    const ulFormat = validateTenorLabelForPeriod(ulTenor, periodLabel);
    if (ulFormat) errors.push(`UL Tenor: ${ulFormat}`);
  }

  const months = Math.max(1, Number(row.months_in_tenor) || 1);
  const subLeg = String(row.sub_leg ?? "").trim();

  if (months > 1) {
    if (!subLeg) {
      errors.push("Sub-leg is required");
    } else {
      const subErr = validateSublegMonthLabel(subLeg);
      if (subErr) errors.push(subErr);
    }
  } else if (subLeg) {
    errors.push("Sub-leg must be empty when # months in tenor is 1");
  }

  const needsStartDate = Boolean(row.enable_start_date);
  const hasStart = row.start_date != null && row.start_date !== "";
  const hasEnd = row.end_date != null && row.end_date !== "";

  if (needsStartDate) {
    if (!hasStart) errors.push("Start date is required");
    if (!hasEnd) errors.push("End date is required");
  } else {
    if (hasStart) errors.push("Start date must be empty when not required");
    if (!hasEnd) errors.push("End date is required");
  }

  return errors;
}

function validateTenorGroupRows(groupRows, rowIndexOffset = 0) {
  const errors = [];
  const sorted = [...groupRows].sort(
    (a, b) =>
      Math.max(0, Number(a.sub_leg_index) || 0) -
      Math.max(0, Number(b.sub_leg_index) || 0)
  );
  const primary = sorted[0];
  const months = Math.max(1, Number(primary?.months_in_tenor) || 1);

  if (months > 1 && groupRows.length !== months) {
    errors.push(
      `Tenor ${primary?.tenor_label || ""} needs ${months} sub-leg row(s), found ${groupRows.length}`
    );
  }

  const subLegs = new Set();
  sorted.forEach((row, index) => {
    const subLeg = String(row.sub_leg ?? "").trim().toLowerCase();
    if (!subLeg) return;
    if (subLegs.has(subLeg)) {
      errors.push(
        `Row ${rowIndexOffset + index + 1}: Duplicate sub-leg ${row.sub_leg}`
      );
    }
    subLegs.add(subLeg);
  });

  return errors;
}

export function validateAllAddTenorRows(rows, shortcutLabel = "") {
  const allErrors = [];
  const signatures = new Map();
  const submitGroups = groupAddTenorRowsForSubmit(rows);

  rows.forEach((row, index) => {
    const rowErrors = validateAddTenorRow(row, shortcutLabel);
    rowErrors.forEach((msg) => allErrors.push(`Row ${index + 1}: ${msg}`));
  });

  submitGroups.forEach((groupRows) => {
    if (groupRows.length > 1 || isAddTenorGroupedRow(groupRows[0])) {
      const firstRowIndex = rows.findIndex((row) => row.key === groupRows[0].key);
      validateTenorGroupRows(groupRows, firstRowIndex).forEach((msg) =>
        allErrors.push(
          firstRowIndex >= 0 ? `Row ${firstRowIndex + 1}: ${msg}` : msg
        )
      );
    }

    const primary = groupRows[0];
    const tenorKey = String(primary?.tenor_label ?? "").trim().toLowerCase();
    const catalog = buildAddTenorCatalogParams(primary);
    const periodId = parseAddTenorCatalogId(primary?.period_type_id);
    if (tenorKey && catalog && periodId != null) {
      const sig = `${catalog.product_id}|${catalog.contract_id}|${catalog.style_id}|${periodId}|${tenorKey}`;
      const firstRowIndex = rows.findIndex((row) => row.key === primary.key);
      if (signatures.has(sig)) {
        allErrors.push(
          `Row ${firstRowIndex + 1}: Duplicate tenor (same as row ${signatures.get(sig) + 1})`
        );
      } else if (firstRowIndex >= 0) {
        signatures.set(sig, firstRowIndex);
      }
    }
  });

  return allErrors;
}

/** @returns {Record<string, string[]>} */
export function validateAddTenorRowsByKey(rows, shortcutLabel = "") {
  const byKey = {};
  const signatures = new Map();
  const submitGroups = groupAddTenorRowsForSubmit(rows);

  rows.forEach((row, index) => {
    const errs = [...validateAddTenorRow(row, shortcutLabel)];
    if (errs.length) byKey[row.key] = errs;
  });

  submitGroups.forEach((groupRows) => {
    if (groupRows.length > 1 || isAddTenorGroupedRow(groupRows[0])) {
      const firstRowIndex = rows.findIndex((row) => row.key === groupRows[0].key);
      validateTenorGroupRows(groupRows, firstRowIndex).forEach((msg) => {
        groupRows.forEach((row) => {
          if (!byKey[row.key]) byKey[row.key] = [];
          byKey[row.key].push(msg);
        });
      });
    }

    const primary = groupRows[0];
    const tenorKey = String(primary?.tenor_label ?? "").trim().toLowerCase();
    const catalog = buildAddTenorCatalogParams(primary);
    const periodId = parseAddTenorCatalogId(primary?.period_type_id);
    if (tenorKey && catalog && periodId != null) {
      const sig = `${catalog.product_id}|${catalog.contract_id}|${catalog.style_id}|${periodId}|${tenorKey}`;
      const firstRowIndex = rows.findIndex((row) => row.key === primary.key);
      if (signatures.has(sig)) {
        const message = `Duplicate tenor (same as row ${signatures.get(sig) + 1})`;
        groupRows.forEach((row) => {
          if (!byKey[row.key]) byKey[row.key] = [];
          byKey[row.key].push(message);
        });
      } else if (firstRowIndex >= 0) {
        signatures.set(sig, firstRowIndex);
      }
    }
  });

  return byKey;
}

export function formatDateForApi(value) {
  if (value == null || value === "") return null;

  if (typeof value === "object" && typeof value.format === "function") {
    return value.format("YYYY-MM-DD");
  }

  if (value instanceof Date) {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
  }

  const parsed = dayjs(value);
  if (parsed.isValid()) {
    return parsed.format("YYYY-MM-DD");
  }

  const str = String(value).trim();
  return str || null;
}
